import { Box, Button, Typography } from "@mui/material";
import { useNavigate } from "@tanstack/react-router";

import { AcceptCollectionStep } from "@/features/Generations/components/AcceptCollectionStep";
import { GenerationsStepper } from "@/features/Generations/components/GenerationsStepper";
import { PublishedState } from "@/features/Generations/components/PublishedState";
import { SetupStep } from "@/features/Generations/components/SetupStep/SetupStep";
import { StartingPointStep } from "@/features/Generations/components/StartingPointStep";
import {
  GENERATIONS_ROUTE_STEPS,
  generationsProgressStepIndex,
  generationsStepIndex,
  generationsStepRoutePath,
  type GenerationsStepId,
} from "@/features/Generations/steps";
import { OrionPageHeader } from "@/orion/OrionComponents/OrionPageHeader";

export type GenerationsStepShellProps = {
  siteId: string;
  stepId: GenerationsStepId;
};

export function GenerationsStepShell({
  siteId,
  stepId,
}: GenerationsStepShellProps) {
  const navigate = useNavigate();

  const stepIndex = generationsStepIndex(stepId);
  const currentStep = GENERATIONS_ROUTE_STEPS[stepIndex];
  const previousStep = GENERATIONS_ROUTE_STEPS[stepIndex - 1];
  const nextStep = GENERATIONS_ROUTE_STEPS[stepIndex + 1];

  const goToStep = (targetStepId: GenerationsStepId) =>
    void navigate({
      to: generationsStepRoutePath(targetStepId),
      params: { siteId, stepId: targetStepId },
    });

  // Steps that render their own layout and navigation controls, so the shell
  // skips its generic header/placeholder and Back/Continue footer.
  const isStandaloneStep =
    stepId === "starting-point" || stepId === "setup" || stepId === "publish";

  // The progress bar appears only once the user is inside the flow: not on the
  // starting point, and on setup only after the blueprint picker (SetupStep
  // renders it above the canvas itself).
  const showStepper = stepId !== "starting-point" && stepId !== "setup";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
      {showStepper && <GenerationsStepper currentStepId={stepId} />}

      {stepId === "starting-point" ? (
        <StartingPointStep siteId={siteId} />
      ) : stepId === "setup" ? (
        <SetupStep siteId={siteId} />
      ) : stepId === "accept-collection" ? (
        <AcceptCollectionStep
          siteId={siteId}
          onPublished={() => nextStep && goToStep(nextStep.id)}
        />
      ) : stepId === "publish" ? (
        <PublishedState siteId={siteId} />
      ) : (
        <>
          <OrionPageHeader
            title={`Step ${generationsProgressStepIndex(stepId) + 1}: ${currentStep.label}`}
            description="Step content is not implemented yet — this is a Phase 1 scaffold of the wizard shell."
          />
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              px: 6,
            }}
          >
            <Typography color="text.secondary">
              {currentStep.label} content coming in a later phase.
            </Typography>
          </Box>
        </>
      )}

      {!isStandaloneStep && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            px: 6,
            py: 4,
            borderTop: 1,
            borderColor: "tokens.outlines.defaultBordersBalanced",
          }}
        >
          <Button
            variant="outlined"
            disabled={!previousStep}
            onClick={() => previousStep && goToStep(previousStep.id)}
          >
            Back
          </Button>
          {/* The acceptance grid owns its forward action ("Save collection",
              which opens the publish confirmation), so the footer omits the
              generic Continue there to keep the modal from being bypassed. */}
          {stepId !== "accept-collection" && (
            <Button
              variant="contained"
              disabled={!nextStep}
              onClick={() => nextStep && goToStep(nextStep.id)}
            >
              Continue
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
}
